import apiService from './api';
import type { ApiResponse } from '../types';

export type PayrollExportTarget = 'myob' | 'xero';

export interface PayrollCategoryMapping {
  internal_category: string;
  external_name: string | null;
  /**
   * Stable identifier for the external payroll item, where the target has one.
   *
   * Xero timesheet lines require an `EarningsRateID` (GUID) — matching by name
   * at push time breaks silently when a rate is renamed in Xero, and is
   * ambiguous when two rates share a name. MYOB matches by name and leaves
   * this null.
   *
   * `external_name` is still kept in step with the selected id so the existing
   * name-based CSV export continues to work unchanged.
   */
  external_id?: string | null;
  export_enabled: boolean;
}

// Union can't be extended; intersect with the extra fields on each branch.
export type PayrollMappingResponse =
  | (ApiResponse<PayrollCategoryMapping[]> & { target?: PayrollExportTarget });

class PayrollMappingService {
  async getMappings(orgId: number, target: PayrollExportTarget): Promise<PayrollMappingResponse> {
    return apiService.get(`/organisations/${orgId}/payroll-mappings?target=${target}`);
  }

  async updateMappings(
    orgId: number,
    target: PayrollExportTarget,
    mappings: PayrollCategoryMapping[]
  ): Promise<PayrollMappingResponse> {
    const body = {
      target,
      mappings: mappings.map(m => ({
        internal_category: m.internal_category,
        external_name:     m.external_name ?? '',
        external_id:       m.external_id ?? null,
        export_enabled:    m.export_enabled,
      })),
    };
    return apiService.put(`/organisations/${orgId}/payroll-mappings`, body);
  }
}

export const payrollMappingService = new PayrollMappingService();
export default payrollMappingService;
